'use client'

import { useEffect } from 'react'
import { useReportWebVitals } from 'next/web-vitals'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

type Pending = { name: string; params: Record<string, unknown> }

/**
 * Metrics observed before gtag.js has finished loading.
 *
 * This buffer is the whole point of the file. `gtag/js` loads
 * `afterInteractive`, but TTFB and FCP are reported almost immediately — so the
 * previous implementation's `window.gtag?.(...)` optional call silently
 * no-opped for exactly the metrics that fire earliest, with no error to notice.
 * Module scope, because `WebVitals` mounts once per layout.
 */
const pending: Pending[] = []

function drain(): boolean {
  if (typeof window.gtag !== 'function') return false
  while (pending.length > 0) {
    const { name, params } = pending.shift()!
    window.gtag('event', name, params)
  }
  return true
}

/**
 * Reports Core Web Vitals (LCP, CLS, INP, FCP, TTFB) to GA4, reusing the
 * existing Google Analytics tag. No extra vendor or package —
 * `useReportWebVitals` ships with Next.js.
 */
export default function WebVitals() {
  useReportWebVitals((metric) => {
    // CLS is unitless; scale for readable integers. Everything else is in ms.
    const scale = metric.name === 'CLS' ? 1000 : 1
    pending.push({
      name: metric.name,
      params: {
        value: Math.round(metric.value * scale),
        metric_id: metric.id,
        metric_value: metric.value,
        metric_rating: metric.rating,
      },
    })
    drain()
  })

  useEffect(() => {
    if (drain()) return

    const poll = window.setInterval(() => {
      if (drain()) window.clearInterval(poll)
    }, 500)
    // gtag.js is either present within a few seconds or blocked outright (ad
    // blockers, consent tooling). Stop polling rather than leak a timer.
    const giveUp = window.setTimeout(() => window.clearInterval(poll), 15_000)

    return () => {
      window.clearInterval(poll)
      window.clearTimeout(giveUp)
    }
  }, [])

  return null
}
